// A KIADÁS-ELEMZÉS szűrő-sávja (user-kérés): a hónap-léptető mellett KERESŐ (szállító neve · adószám ·
// számlaszám) és szűrők — TÍPUS (mind / bér / egyéb), KATEGÓRIA (a típus szerint szűkítve), KIBOCSÁTÓ, FORRÁS.
//
// Az állapot az URL-ben él (`xa_` előtag — a Kiadások fül `exp_` szűrőitől függetlenül: a lista szűrése nem
// írhatja át az elemzést, és fordítva). A kereső debounce-olt: a lekérdezés kulcsa a szűrő-állapot.
//
// A KATEGÓRIA a Kiadások lista mintájára EGYVÁLASZTÓS, behúzott fa (egy csomópont az egész ágát hozza — a BFF
// bontja ki), a `none` a besorolatlan. A TÍPUS szűkíti: „bér" mellett csak a bér-típusú tételt TARTALMAZÓ ágak,
// „egyéb" mellett a nem bér tételt tartalmazók maradnak — és típus-váltáskor a kategória törlődik, hogy ne
// maradhasson érvénytelen (üres eredményt adó) párosítás.

import type { ExpenseAnalyticsQuery, ExpenseCategoryRow } from '@atlas/contracts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ALL_FILTER_VALUE as ALL, type ToolbarFilterItem } from '@/components/shared/list-toolbar';
import {
  categoryOptions,
  EXPENSE_ISSUERS,
  expenseKeys,
  UNCATEGORIZED,
} from '@/features/expenses/api';
import { useDebouncedSearch } from '@/hooks/use-debounced-search';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';

type ExpenseType = 'salary' | 'other';

const DEFAULTS = {
  xa_q: '',
  xa_type: ALL,
  xa_cat: ALL,
  xa_issuer: ALL,
  xa_source: ALL,
};

/**
 * A kategória-fa csomópontjai, amelyek a típushoz illenek: az ág (a csomópont + leszármazottai) tartalmaz-e
 * bér-típusú, illetve nem bér LEVELET. A bér-jelölés CSAK levélen él, nem öröklődik.
 */
export function categoriesForType(
  rows: readonly ExpenseCategoryRow[],
  type: ExpenseType,
): ExpenseCategoryRow[] {
  const children = new Map<string, string[]>();
  for (const r of rows) {
    if (r.parentId) children.set(r.parentId, [...(children.get(r.parentId) ?? []), r.id]);
  }
  const byId = new Map(rows.map((r) => [r.id, r]));
  const memo = new Map<string, { salary: boolean; other: boolean }>();
  const visit = (id: string, seen: Set<string>): { salary: boolean; other: boolean } => {
    const cached = memo.get(id);
    if (cached) return cached;
    if (seen.has(id)) return { salary: false, other: false }; // ciklus-védelem
    seen.add(id);
    const kids = children.get(id) ?? [];
    const self = byId.get(id);
    const result =
      kids.length === 0
        ? { salary: self?.requiresEmployee === true, other: self?.requiresEmployee !== true }
        : kids
            .map((k) => visit(k, seen))
            .reduce((a, b) => ({ salary: a.salary || b.salary, other: a.other || b.other }), {
              salary: false,
              other: false,
            });
    memo.set(id, result);
    return result;
  };
  return rows.filter((r) => visit(r.id, new Set())[type]);
}

export function useExpenseAnalyticsFilters({ enabled }: { enabled: boolean }): {
  filterItems: ToolbarFilterItem[];
  searchInput: string;
  setSearchInput: (v: string) => void;
  /** A BFF-nek menő szűrők (a hónap nélkül) — üres objektum, ha nincs szűkítés. */
  query: Omit<ExpenseAnalyticsQuery, 'month' | 'lang'>;
} {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { filters: url, setFilters } = useListFilters(DEFAULTS);

  const commitSearch = useCallback((v: string) => setFilters({ xa_q: v }), [setFilters]);
  const { input: searchInput, setInput: setSearchInput } = useDebouncedSearch(
    url.xa_q,
    commitSearch,
  );

  const categoriesQ = api.expenses.categories.useQuery(expenseKeys.categories(), undefined, {
    queryKey: expenseKeys.categories(),
    enabled,
  });
  const categories = useMemo(
    () => (categoriesQ.data?.status === 200 ? categoriesQ.data.body.rows : []),
    [categoriesQ.data],
  );

  const type = url.xa_type === 'salary' || url.xa_type === 'other' ? url.xa_type : undefined;
  const visibleCategories = type === undefined ? categories : categoriesForType(categories, type);

  const filterItems: ToolbarFilterItem[] = [
    {
      kind: 'select',
      key: 'type',
      label: t('finance.expenseFilters.type'),
      value: url.xa_type,
      // típus-váltáskor a kategória törlődik: a régi választás az új típus mellett üres eredményt adhatna
      onChange: (v: string) => setFilters({ xa_type: v, xa_cat: ALL }),
      options: [
        { value: 'salary', label: t('finance.expenseFilters.salary') },
        { value: 'other', label: t('finance.expenseFilters.other') },
      ],
      allValue: ALL,
    },
    {
      kind: 'select',
      key: 'category',
      label: t('expenses.filters.category'),
      value: url.xa_cat,
      onChange: (v: string) => setFilters({ xa_cat: v }),
      options: [
        // a besorolatlan sor nem lehet bér-típusú — „bér" mellett nincs mit felkínálni
        ...(type === 'salary'
          ? []
          : [{ value: UNCATEGORIZED, label: t('expenses.uncategorized') }]),
        ...categoryOptions(visibleCategories, locale),
      ],
      allValue: ALL,
    },
    {
      kind: 'select',
      key: 'issuer',
      label: t('expenses.filters.issuer'),
      value: url.xa_issuer,
      onChange: (v: string) => setFilters({ xa_issuer: v }),
      options: EXPENSE_ISSUERS.map((k) => ({
        value: k,
        label: t(`invoices.issuerLabel.${k}`, { defaultValue: k }),
      })),
      allValue: ALL,
    },
    {
      kind: 'select',
      key: 'source',
      label: t('expenses.filters.source'),
      value: url.xa_source,
      onChange: (v: string) => setFilters({ xa_source: v }),
      options: [
        { value: 'nav', label: t('expenses.source.nav') },
        { value: 'manual', label: t('expenses.source.manual') },
      ],
      allValue: ALL,
    },
  ];

  const query: Omit<ExpenseAnalyticsQuery, 'month' | 'lang'> = {
    ...(url.xa_q.trim() !== '' ? { q: url.xa_q.trim() } : {}),
    ...(type !== undefined ? { type } : {}),
    ...(url.xa_cat !== ALL ? { categoryId: [url.xa_cat] } : {}),
    ...(url.xa_issuer !== ALL ? { issuerKey: url.xa_issuer } : {}),
    ...(url.xa_source === 'nav' || url.xa_source === 'manual' ? { source: url.xa_source } : {}),
  };

  return { filterItems, searchInput, setSearchInput, query };
}
